import React, { useState, useEffect, useMemo } from "react";
import {
  EuiPage,
  EuiPageBody,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiTitle,
  EuiText,
  EuiButton,
  EuiCard,
  EuiIcon,
  EuiSpacer,
  EuiBadge,
  EuiButtonEmpty,
  EuiAvatar,
  EuiHeader,
  EuiHeaderSection,
  EuiHeaderSectionItem,
  EuiHeaderLogo,
  EuiEmptyPrompt,
  EuiPageContentBody,
  EuiBreadcrumb,
  EuiHeaderSections,
  EuiPageHeader,
} from "@elastic/eui";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { OnboardingProcess, Tenant, User, UserData } from "./types";
import { OnboardingList } from "./onboarding-list";
import { CreateOnboardingModal } from "./new-onboarding";
import { ReviewPanel } from "./review";
import { OnboardingWizard } from "./wizard";
import { TenantDashboard } from "./tenant";
import { AutonubilSvg } from "../../assets";
import { useLocation, useNavigate } from "react-router";
import { useAnketaAuth } from "../../auth";
import { ButtonUserFeedback } from "../../feedback";
import { QuestionnaireHeader } from "../../questionnaires";
import { Avatar } from "../../navigation/header/controls";
import { MicrosoftAuth } from "./auth";
import { useAnketaI18n } from "src/internationalization";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Mock Data
const mockTenants: Tenant[] = [
  {
    id: "1",
    name: "Acme Corporation",
    domain: "acme.com",
    activeUsers: 245,
    licensedUsers: 300,
    activeOnboardings: 5,
    completedOnboardings: 89,
  },
  {
    id: "2",
    name: "TechStart GmbH",
    domain: "techstart.de",
    activeUsers: 128,
    licensedUsers: 150,
    activeOnboardings: 3,
    completedOnboardings: 45,
  },
  {
    id: "3",
    name: "Global Industries",
    domain: "global-ind.com",
    activeUsers: 567,
    licensedUsers: 600,
    activeOnboardings: 8,
    completedOnboardings: 234,
  },
];

const mockUsers: User[] = [
  {
    id: "1",
    name: "Anna Schmidt",
    email: "anna.schmidt@company.com",
    role: "agent",
  },
  {
    id: "2",
    name: "Max Müller",
    email: "max.mueller@company.com",
    role: "reviewer",
  },
  {
    id: "3",
    name: "Sarah Johnson",
    email: "sarah.j@company.com",
    role: "agent",
  },
  {
    id: "4",
    name: "Tom Weber",
    email: "tom.weber@company.com",
    role: "reviewer",
  },
];

const mockOnboardings: OnboardingProcess[] = [
  {
    id: "1",
    name: "Marketing Team Onboarding Q4",
    tenantId: "1",
    tenantName: "Acme Corporation",
    agent: "Anna Schmidt",
    reviewer: "Max Müller",
    status: "pending_review",
    createdAt: "2025-10-20",
  },
  {
    id: "2",
    name: "Sales Department Expansion",
    tenantId: "2",
    tenantName: "TechStart GmbH",
    agent: "Sarah Johnson",
    reviewer: "Tom Weber",
    status: "approved",
    createdAt: "2025-10-15",
  },
];

export const Header = ({
  breadcrumbs,
  navIsOpen,
  setNavIsOpen,
  onLogout,
}: {
  breadcrumbs: EuiBreadcrumb[];
  navIsOpen: boolean;
  setNavIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onLogout: () => void;
}): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useAnketaI18n();

  const { user } = useAnketaAuth();

  const [modalIsVisible, setModalIsVisible] = useState<boolean>(false);

  const currentView = useMemo<string>(() => {
    return location.pathname.split("/")[1];
  }, [location]);

  const sections: EuiHeaderSections[] = [
    {
      items: [
        <EuiHeaderLogo
          iconType={AutonubilSvg}
          onClick={() => {
            setNavIsOpen((isOpen) => !isOpen);
            localStorage.setItem(
              "EuiCollapsibleNavExample--isDocked",
              JSON.stringify(!navIsOpen)
            );
          }}
          aria-label={translate(
            "anketa.medocino.support.header.home.ariaLabel",
            { defaultMessage: "Navigate to home page" }
          )}
        />,
      ],
      borders: "right",
      breadcrumbs: breadcrumbs,
      breadcrumbProps: {
        "aria-label": translate(
          "anketa.medocino.support.header.breadcrumb.ariaLabel",
          { defaultMessage: "Navigate Back to the Homepage" }
        ),
      },
    },
    {
      items: [
        user && <ButtonUserFeedback />,
        currentView === "questionnaires" && (
          <QuestionnaireHeader setModalIsVisible={setModalIsVisible} />
        ),
      ],
      borders: "left",
    },

    {
      items: [
        <Avatar
          additionalComponent={
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty size="s" onClick={onLogout}>
                {translate("anketa.medocino.support.header.logout", {
                  defaultMessage: "Logout from Microsoft",
                })}
              </EuiButtonEmpty>
            </EuiFlexItem>
          }
        />,
      ],
      borders: "left",
    },
  ];

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/home");
    }
    localStorage.setItem("anketa.nav.side.isOpen", navIsOpen.toString());
  }, [navIsOpen]);

  return (
    <EuiPageHeader
      style={{
        position: "sticky",
        top: "15px",
        zIndex: "1000",
      }}
    >
      <EuiHeader
        sections={sections}
        style={{
          width: "100%",
        }}
      />
    </EuiPageHeader>
  );
};

export const OnboardingApp = ({
  breadcrumbs,
}: {
  breadcrumbs: EuiBreadcrumb[];
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const [view, setView] = useState<"tenants" | "onboardings" | "dashboard">(
    "tenants"
  );
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedOnboarding, setSelectedOnboarding] =
    useState<OnboardingProcess | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [onboardings, setOnboardings] = useState(mockOnboardings);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const initialNavIsOpen = useMemo(() => {
    const stored = localStorage.getItem("anketa.nav.side.isOpen");
    return stored ? stored === "true" : false;
  }, []);

  const [navIsOpen, setNavIsOpen] = useState<boolean>(initialNavIsOpen);

  const handleLogin = () => {
    setCurrentUser(mockUsers[1]);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    setView("tenants");
  };

  const handleSelectTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setView("dashboard");
  };

  const handleCreateOnboarding = (onboarding: Partial<OnboardingProcess>) => {
    setOnboardings([...onboardings, onboarding as OnboardingProcess]);
    setShowCreateModal(false);
    setSelectedOnboarding(onboarding as OnboardingProcess);
    setShowWizard(true);
  };

  const handleCompleteWizard = (data: UserData) => {
    if (selectedOnboarding) {
      const updated = onboardings.map((o) =>
        o.id === selectedOnboarding.id
          ? { ...o, userData: data, status: "pending_review" as const }
          : o
      );
      setOnboardings(updated);
    }
    setShowWizard(false);
    setSelectedOnboarding(null);
  };

  const handleApprove = () => {
    if (selectedOnboarding) {
      const updated = onboardings.map((o) =>
        o.id === selectedOnboarding.id
          ? { ...o, status: "approved" as const }
          : o
      );
      setOnboardings(updated);
      setSelectedOnboarding(null);
    }
  };

  const handleReject = () => {
    if (selectedOnboarding) {
      const updated = onboardings.map((o) =>
        o.id === selectedOnboarding.id
          ? { ...o, status: "rejected" as const }
          : o
      );
      setOnboardings(updated);
      setSelectedOnboarding(null);
    }
  };

  const handleEditOnboarding = () => {
    setSelectedOnboarding(selectedOnboarding);
    setShowWizard(true);
  };

  const handleAuthSuccess = (user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  if (!isAuthenticated) {
    return <MicrosoftAuth onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <>
      <Header
        onLogout={handleLogout}
        breadcrumbs={breadcrumbs}
        navIsOpen={navIsOpen}
        setNavIsOpen={setNavIsOpen}
      />
      <EuiPage paddingSize="l" style={{ marginTop: "48px" }}>
        <EuiPageBody>
          <EuiPageContentBody>
            <EuiTitle size="l">
              <h1>
                {translate("anketa.medocino.support.app.title", {
                  defaultMessage: "Onboarding System",
                })}
              </h1>
            </EuiTitle>
            <EuiSpacer />

            {view === "tenants" && (
              <>
                <EuiTitle size="m">
                  <h2>
                    {translate("anketa.medocino.support.tenants.select.title", {
                      defaultMessage: "Select tenant",
                    })}
                  </h2>
                </EuiTitle>
                <EuiSpacer />
                <EuiFlexGroup gutterSize="l">
                  {mockTenants.map((tenant) => (
                    <EuiFlexItem key={tenant.id}>
                      <EuiCard
                        icon={<EuiIcon size="xxl" type="storage" />}
                        title={tenant.name}
                        description={translate(
                          "anketa.medocino.support.tenant.card.description",
                          {
                            values: {
                              domain: tenant.domain,
                              count: tenant.activeUsers,
                            },
                            defaultMessage: "{{domain}} • {{count}} active users",
                          }
                        )}
                        onClick={() => handleSelectTenant(tenant)}
                        footer={
                          <EuiFlexGroup gutterSize="s">
                            <EuiFlexItem>
                              <EuiBadge color="success">
                                {translate(
                                  "anketa.medocino.support.tenant.card.active",
                                  {
                                    values: {
                                      count: tenant.activeOnboardings,
                                    },
                                    defaultMessage: "{{count}} active",
                                  }
                                )}
                              </EuiBadge>
                            </EuiFlexItem>
                            <EuiFlexItem>
                              <EuiBadge color="primary">
                                {translate(
                                  "anketa.medocino.support.tenant.card.completed",
                                  {
                                    values: {
                                      count: tenant.completedOnboardings,
                                    },
                                    defaultMessage: "{{count}} completed",
                                  }
                                )}
                              </EuiBadge>
                            </EuiFlexItem>
                          </EuiFlexGroup>
                        }
                      />
                    </EuiFlexItem>
                  ))}
                </EuiFlexGroup>
              </>
            )}

            {view === "dashboard" && selectedTenant && (
              <>
                <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
                  <EuiFlexItem grow={false}>
                    <EuiTitle size="m">
                      <h2>{selectedTenant.name}</h2>
                    </EuiTitle>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonEmpty
                      iconType="arrowLeft"
                      onClick={() => {
                        setSelectedTenant(null);
                        setView("tenants");
                      }}
                    >
                      {translate("anketa.medocino.support.back.toTenants", {
                        defaultMessage: "Back to tenants",
                      })}
                    </EuiButtonEmpty>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer />
                <TenantDashboard
                  tenant={selectedTenant}
                  onStartOnboarding={() => setShowCreateModal(true)}
                  onViewOnboardings={() => setView("onboardings")}
                />
              </>
            )}

            {view === "onboardings" && (
              <>
                <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
                  <EuiFlexItem grow={false}>
                    <EuiTitle size="m">
                      <h2>
                        {translate("anketa.medocino.support.onboardings.all.title", {
                          defaultMessage: "All onboardings",
                        })}
                      </h2>
                    </EuiTitle>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiFlexGroup gutterSize="s">
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          fill
                          iconType="plus"
                          onClick={() => setShowCreateModal(true)}
                        >
                          {translate("anketa.medocino.support.onboarding.new", {
                            defaultMessage: "New onboarding",
                          })}
                        </EuiButton>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButtonEmpty
                          iconType="arrowLeft"
                          onClick={() => setView("dashboard")}
                        >
                          {translate("anketa.medocino.support.back.toDashboard", {
                            defaultMessage: "Back to dashboard",
                          })}
                        </EuiButtonEmpty>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer />

                {selectedOnboarding && (
                  <ReviewPanel
                    onboarding={selectedOnboarding}
                    currentUser={currentUser}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onEdit={() => handleEditOnboarding()}
                  />
                )}

                <EuiPanel>
                  <OnboardingList
                    onboardings={onboardings}
                    onSelectOnboarding={(onboarding) => {
                      setSelectedOnboarding(onboarding);
                      if (onboarding.status === "draft") {
                        setShowWizard(true);
                      }
                    }}
                    currentUser={currentUser}
                  />
                </EuiPanel>
              </>
            )}

            {showCreateModal && (
              <CreateOnboardingModal
                tenants={mockTenants}
                users={mockUsers}
                onClose={() => setShowCreateModal(false)}
                onCreate={handleCreateOnboarding}
              />
            )}

            {showWizard && selectedOnboarding && (
              <OnboardingWizard
                onboarding={selectedOnboarding}
                onClose={() => {
                  setShowWizard(false);
                  setSelectedOnboarding(null);
                }}
                onComplete={handleCompleteWizard}
                currentUser={currentUser}
              />
            )}
          </EuiPageContentBody>
        </EuiPageBody>
      </EuiPage>
    </>
  );
};
